import React from 'react';
import { BaseButtonProps, Button } from '@zr/zds/src/components/Button';
import { Icon } from '@zr/zds/src/components/Icon';

export type Page = {
  pageNumber: number;
  href?: string;
  onClick?: (pageNumber: number) => void;
};

export type PaginationProps = {
  variant: 'simple' | 'heavy';
  pages: Array<Page>;
  currentPage: number;
  maxPages: number;
  pageSiblings?: number;
  // eslint-disable-next-line react/no-unused-prop-types
  pageSize?: number;
  // eslint-disable-next-line react/no-unused-prop-types
  resultCount?: number;
};

export const Pagination = ({
  variant = 'simple',
  pages,
  currentPage,
  maxPages,
  pageSiblings = 2,
}: PaginationProps): JSX.Element | null => {
  const clampedCurrentPage = currentPage > 0 ? Math.min(currentPage, maxPages) : 0;
  const currentPageIndex = clampedCurrentPage - 1;
  const firstPageIndex = 0;
  const lastPageIndex = maxPages - 1;
  const leftSiblingIndex = Math.max(currentPageIndex - pageSiblings, firstPageIndex);
  const rightSiblingIndex = Math.min(currentPageIndex + pageSiblings, lastPageIndex);
  const previousPageIndex = Math.max(currentPageIndex - 1, firstPageIndex);
  const nextPageIndex = Math.min(currentPageIndex + 1, lastPageIndex);
  const currentPageAndSiblings = pages.slice(leftSiblingIndex, rightSiblingIndex + 1);

  const getButtonVariant = (p: Page): BaseButtonProps['variant'] | undefined => {
    const isCurrentPage = p.pageNumber === clampedCurrentPage;
    if (variant === 'simple') {
      return isCurrentPage ? 'primary' : 'ghost';
    }
    if (variant === 'heavy') {
      return isCurrentPage ? 'primary' : 'secondary';
    }
    return undefined;
  };

  const getClickHandler = (p: Page): (() => void) => () => p.onClick?.(p.pageNumber);

  const getPageBtnJSX = (p: Page): JSX.Element => (
    <div key={p.pageNumber} style={{ width: 48, height: 48 }}>
      <Button
        variant={getButtonVariant(p)}
        title={`Page: ${p.pageNumber}`}
        fullWidth
        onClick={getClickHandler(p)}
        href={p.href}
      >
        {p.pageNumber}
      </Button>
    </div>
  );

  const getEllipsisJSX = (key: string) => (
    <div key={key} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: 12 }}>
      <Icon name="ellipseHorizontal" size="medium" />
    </div>
  );

  const buildPageBtnsJSX = () => {
    const pageBtnsJSX = currentPageAndSiblings.map((p) => getPageBtnJSX(p));
    if (currentPageAndSiblings.length < pages.length) {
      if (leftSiblingIndex >= firstPageIndex + 1) {
        if (leftSiblingIndex - 1 > firstPageIndex) {
          pageBtnsJSX.unshift(getEllipsisJSX('left-ellipsis'));
        }
        pageBtnsJSX.unshift(getPageBtnJSX(pages[firstPageIndex]));
      }
      if (rightSiblingIndex <= lastPageIndex - 1) {
        if (rightSiblingIndex + 1 < lastPageIndex) {
          pageBtnsJSX.push(getEllipsisJSX('right-ellipsis'));
        }
        pageBtnsJSX.push(getPageBtnJSX(pages[lastPageIndex]));
      }
    }
    return pageBtnsJSX;
  };

  return (
    <div className="flex flex-row" style={{ gap: 12 }}>
      <div style={{ width: 48, height: 48 }}>
        <Button
          variant={variant === 'simple' ? 'secondary' : 'ghost'}
          icon="chevronLeft"
          title="Previous Page"
          fullWidth
          disabled={currentPageIndex === firstPageIndex}
          onClick={getClickHandler(pages[previousPageIndex])}
          href={pages[previousPageIndex]?.href}
        />
      </div>
      {buildPageBtnsJSX()}
      <div style={{ width: 48, height: 48 }}>
        <Button
          variant={variant === 'simple' ? 'secondary' : 'ghost'}
          icon="chevronRight"
          title="Next Page"
          fullWidth
          disabled={currentPageIndex === lastPageIndex}
          onClick={getClickHandler(pages[nextPageIndex])}
          href={pages[nextPageIndex]?.href}
        />
      </div>
    </div>
  );
};
